/**
 * Что агент собирает — витринный срез каталога шаблонов.
 *
 * Правда о каталоге живёт в `apps/api/src/db/seed-templates.ts`; здесь её
 * пересказ для тех, кто ещё не вошёл. Пересказ, а не запрос: публичного
 * маршрута для каталога нет и заводить его ради лендинга незачем — состав
 * меняется не чаще, чем сам лендинг.
 *
 * Зато один на всех: раньше этих чисел не было вовсе, а появившись в четырёх
 * местах — на странице, в частых вопросах, в llms.txt и в schema.org — они
 * разошлись бы на первом же новом шаблоне. Добавили шаблон в засев — поправьте
 * здесь, и поправится везде.
 *
 * Порядок не алфавитный и не по числу шаблонов: он по тому, как продукт
 * объясняется. Лендинг первым, потому что это самая большая группа каталога и
 * при этом та, о которой лендинг раньше не говорил ни слова.
 */
export interface DocKind {
  /** Название типа — оно же поисковый запрос, которым его ищут. */
  name: string;
  /** Сколько шаблонов этого типа в каталоге. */
  count: number;
  /** Чем этот тип полезен. Одна фраза, без обещаний, которых продукт не даёт. */
  note: string;
  /** Отраслевые заготовки — их названия и есть длинный хвост запросов. */
  examples?: string;
  /**
   * Посадочная страница про этот тип, если она есть.
   *
   * Ссылка отсюда — не украшение: она связывает витрину с посадочными
   * страницами, и поиск разбирает их как раздел одного сайта, а не как
   * отдельные документы.
   */
  href?: string;
}

export const DOC_KINDS: DocKind[] = [
  {
    name: 'Лендинг',
    href: '/landing',
    count: 10,
    note: 'Страница с формой заявки: заявки приходят вам, а открыть её можно на своём домене.',
    examples:
      'продукт, SaaS-сервис, онлайн-школа, клиника, кофейня, спортзал, жилой комплекс, ' +
      'студия, ферма',
  },
  {
    name: 'Коммерческое предложение',
    href: '/kp',
    count: 1,
    note: 'Смета из прайса, сроки из записи созвона, условия из брифа. Видно, что клиент открыл ссылку.',
  },
  {
    name: 'Презентация компании',
    href: '/presentations',
    count: 2,
    note: 'О компании, услуги, кейсы, команда. Той же кнопкой выгружается в PPTX и PDF.',
  },
  {
    name: 'Прайс-лист',
    count: 2,
    note: 'Розничный и оптовый. Поправили цену — ссылка у клиента сразу показывает новую.',
  },
  {
    name: 'Кейс проекта',
    count: 1,
    note: 'Задача, решение, результат — из материалов, которые уже лежат в переписке.',
  },
  {
    name: 'Меню кафе и ресторана',
    count: 2,
    note: 'Разделы, позиции, цены. Меняется за минуты и не ждёт новой печати.',
  },
  {
    name: 'Флаер и афиша',
    count: 5,
    note: 'Выгружается в PDF — и печатается, и уходит ссылкой.',
    examples: 'доставка, магазин, услуги, барбершоп, ярмарка',
  },
  {
    name: 'Отчёт для клиента',
    count: 1,
    note: 'Что сделано за период — по пунктам и в цифрах.',
  },
  {
    name: 'Счёт и договор-оферта',
    count: 2,
    note: 'Реквизиты, позиции, условия. Уходит ссылкой или файлом.',
  },
];

/**
 * Всего шаблонов в каталоге.
 *
 * Единица сверху — «Чистый лист»: он в каталоге есть, но своей карточкой на
 * витрине не стоит. Обещать «27 шаблонов» и показать 26 — ровно тот случай,
 * когда посчитавший заметит.
 */
export const TEMPLATE_TOTAL = DOC_KINDS.reduce((sum, kind) => sum + kind.count, 0) + 1;

/** Сколько шаблонов лендингов. Число называют и страница, и частые вопросы. */
export const LANDING_COUNT = DOC_KINDS[0]?.count ?? 0;
